import { HasChildren } from "@/types/shared";
import { Dialog, DialogPanelProps } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MotionProps, motion } from "framer-motion";
import { ElementType, Fragment, ReactNode } from "react";
import tw from "twin.macro";
import { Transition } from "../Transition";

export const ModalButton = tw.button`inline-flex flex-1 justify-center items-center rounded-xl bg-white py-3 text-xl font-semibold text-primary-800 shadow-sm disabled:opacity-50`;

export type BaseModalProps = HasChildren & {
  open: boolean;
  onClose: () => void;
};

export const Modal = ({
  children,
  open,
  onClose,
  ...props
}: BaseModalProps) => (
  <Transition show={open} as={Fragment}>
    <Dialog as="div" tw="relative z-10" onClose={onClose}>
      {/* Backdrop */}
      <Transition.Child
        as={"div"}
        aria-hidden="true"
        enter={tw`ease-out duration-300`}
        enterFrom={tw`opacity-0`}
        enterTo={tw`opacity-100`}
        leave={tw`ease-in duration-200`}
        leaveFrom={tw`opacity-100`}
        leaveTo={tw`opacity-0`}
        tw="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
      />

      {/* Panel */}
      <Transition.Child
        as={Dialog.Panel}
        enter={tw`ease-out duration-300`}
        enterFrom={tw`opacity-0`}
        enterTo={tw`opacity-100 `}
        leave={tw`ease-in duration-200`}
        leaveFrom={tw`opacity-100 `}
        leaveTo={tw`opacity-0`}
        tw="fixed inset-x-4 top-1/2 -translate-y-1/2 max-w-screen-sm mx-auto z-10 overflow-y-auto rounded-xl rounded-tr-none flex flex-col items-center justify-center gap-10 bg-white text-black p-10"
        {...props}
      >
        {children}
      </Transition.Child>
    </Dialog>
  </Transition>
);

export const SlideBackdrop = ({
  open,
  onClose,
  children,
  onBackdropContent,
}: BaseModalProps & {
  onBackdropContent?: ReactNode;
}) => (
  <Transition show={open} as={Fragment}>
    <Dialog as="div" tw="relative z-10" onClose={onClose}>
      <Transition.Child
        as={"div"}
        enter={tw`ease-in-out duration-500`}
        enterFrom={tw`opacity-0`}
        enterTo={tw`opacity-100`}
        leave={tw`ease-in-out duration-500`}
        leaveFrom={tw`opacity-100`}
        leaveTo={tw`opacity-0`}
        tw="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
      />

      {onBackdropContent}

      {children}
    </Dialog>
  </Transition>
);

export type SlideOverProps<TTag extends ElementType = "div"> =
  DialogPanelProps<TTag> &
    BaseModalProps & {
      title?: string;
      buttons?: ReactNode;
      withTopRightCloseButton?: boolean;
    };

export type SlideOverPropsWithData<T> = SlideOverProps & { data: T };

export const SlideOver = ({
  open,
  onClose,
  title,
  buttons,
  withTopRightCloseButton = false,
  children,
  ...props
}: SlideOverProps) => (
  <SlideBackdrop onClose={onClose} open={open}>
    <SlideOverModalContent
      open={open}
      onClose={onClose}
      buttons={buttons}
      title={title}
      withTopRightCloseButton={withTopRightCloseButton}
      {...props}
    >
      {children}
    </SlideOverModalContent>
  </SlideBackdrop>
);

export const SlideOverModalContent = ({
  open,
  onClose,
  title,
  buttons,
  withTopRightCloseButton = false,
  children,
  ...props
}: SlideOverProps) => (
  <div tw="fixed inset-0 overflow-hidden">
    <div tw="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
      <Transition.Child
        as={Fragment}
        enter={tw`transform transition ease-in-out duration-500`}
        enterFrom={tw`translate-x-full`}
        enterTo={tw`translate-x-0`}
        leave={tw`transform transition ease-in-out duration-300`}
        leaveFrom={tw`translate-x-0`}
        leaveTo={tw`translate-x-full`}
      >
        <Dialog.Panel
          tw="pointer-events-auto w-screen max-w-2xl flex h-full flex-col overflow-y-auto bg-white shadow-xl"
          {...props}
        >
          {/* Panel content */}
          <div tw="flex-1">
            {/* Header */}
            <div tw="flex items-start justify-between space-x-3 px-4 py-6 sm:px-6">
              <div tw="space-y-1">
                <Dialog.Title tw="text-base font-semibold leading-6 text-gray-900">
                  {title}
                </Dialog.Title>
              </div>

              {withTopRightCloseButton && (
                <button
                  type="button"
                  tw="relative flex h-7 items-center text-gray-400 hover:text-gray-500"
                  onClick={onClose}
                >
                  <span tw="sr-only">Close panel</span>
                  <XMarkIcon tw="h-6 w-6" aria-hidden="true" />
                </button>
              )}
            </div>

            {children}
          </div>

          {/* Action buttons */}
          {buttons && (
            <div tw="shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6 flex justify-end space-x-3">
              {buttons}
            </div>
          )}
        </Dialog.Panel>
      </Transition.Child>
    </div>
  </div>
);

export const BottomModal = {
  Button: tw.button``,
  Container: ({ children, open, onClose, ...props }: BaseModalProps) => (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" tw="relative z-40" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={"div"}
          // @ts-expect-error : transition is not polymorphic
          onClick={onClose}
          aria-hidden="true"
          enter={tw`ease-out duration-300`}
          enterFrom={tw`opacity-0`}
          enterTo={tw`opacity-100`}
          leave={tw`ease-in duration-200`}
          leaveFrom={tw`opacity-100`}
          leaveTo={tw`opacity-0`}
          tw="fixed inset-0 bg-black bg-opacity-80 transition-opacity"
        />

        {/* Panel */}
        <Transition.Child
          as={Dialog.Panel}
          enter={tw`ease-out duration-300`}
          enterFrom={tw`opacity-0 translate-y-4`}
          enterTo={tw`opacity-100 translate-y-0`}
          leave={tw`ease-in duration-200`}
          leaveFrom={tw`opacity-100 translate-y-0`}
          leaveTo={tw`opacity-0 translate-y-4`}
          tw="fixed bottom-0 inset-x-0 z-10 overflow-y-auto rounded-t-5xl flex flex-col items-center justify-center gap-12 bg-primary-800 text-white px-12 pb-12 h-full"
          {...props}
        >
          <button onClick={onClose} tw="p-6 bg-white/10 rounded-b-3xl">
            <XMarkIcon tw="h-8 w-8 stroke-2" />
          </button>

          {children}
        </Transition.Child>
      </Dialog>
    </Transition>
  ),
};

export type SlideUpModalProps = HasChildren &
  MotionProps & {
    open: boolean;
    onClose: () => void;
    onBackdropContent?: ReactNode;
    closeTranslation?: string;
  };

export type SlideModalProps = SlideUpModalProps & SlideOverProps;
const spring = {
  type: "spring",
  stiffness: 400,
  damping: 30,
  delay: 0.2,
};

export const SlideUpModal = ({
  children,
  onBackdropContent,
  closeTranslation = "100%",
  open,
  onClose,
  ...props
}: SlideUpModalProps) => (
  <SlideBackdrop
    onClose={onClose}
    open={open}
    onBackdropContent={onBackdropContent}
  >
    <SlideUpModalContent open={open} onClose={onClose} {...props}>
      {children}
    </SlideUpModalContent>
  </SlideBackdrop>
);

export const SlideUpModalContent = ({
  children,
  closeTranslation = "100%",
  open,
  onClose,
  ...props
}: SlideUpModalProps) => (
  <motion.div
    layout
    transition={spring}
    initial={{ opacity: "100%" }}
    animate={{ opacity: open ? "0%" : closeTranslation }}
    exit={{ opacity: "100%" }}
    tw="fixed bottom-48 inset-x-3 overflow-y-auto translate-y-full rounded-5xl flex flex-col items-center justify-center gap-6 bg-white text-gray-800 px-12 pb-12"
    {...props}
  >
    <button onClick={onClose} tw="p-3 bg-gray-100 rounded-b-xl">
      <XMarkIcon tw="h-6 w-auto stroke-2" />
    </button>

    {children}
  </motion.div>
);

export const SlideModal = ({
  children,
  onBackdropContent,
  closeTranslation = "100%",
  open,
  onClose,
  buttons,
  ...props
}: SlideModalProps) => {
  return (
    <SlideBackdrop
      onClose={onClose}
      open={open}
      onBackdropContent={onBackdropContent}
    >
      {/* Mobile modal */}
      <SlideUpModalContent
        layoutId="mobileSlideModal"
        onClose={onClose}
        open={open}
        tw="flex md:hidden"
        {...props}
      >
        {children}
      </SlideUpModalContent>

      {/* Desktop modal */}
      <SlideOverModalContent
        layoutId="desktopSlideModal"
        onClose={onClose}
        open={open}
        buttons={buttons}
        tw="hidden md:flex"
        {...props}
      >
        {children}
      </SlideOverModalContent>
    </SlideBackdrop>
  );
};
